import { useState, useEffect, useCallback } from "react";
import { 
  Package, Plus, Search, Warehouse, TrendingUp, TrendingDown,
  ArrowUpDown, Loader2, Edit2, Trash2, AlertTriangle, 
  BarChart3, Clock, Box, Tag, FileText, ClipboardList,
  Save, X, Check, RefreshCw, AlertCircle, ChevronRight
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { 
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription
} from "../components/ui/dialog";
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { authFetch } from "../services/authService";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Magazzino({ user }) {
  const [activeTab, setActiveTab] = useState("articoli");
  const [articoli, setArticoli] = useState([]);
  const [servizi, setServizi] = useState([]);
  const [movimenti, setMovimenti] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [showOnlySottoScorta, setShowOnlySottoScorta] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showMovimentoDialog, setShowMovimentoDialog] = useState(false);
  const [showInventarioDialog, setShowInventarioDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [moduleConfig, setModuleConfig] = useState(null);
  const [inventarioData, setInventarioData] = useState([]);
  const [inventarioLoading, setInventarioLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    codice: "",
    descrizione: "",
    prezzo_vendita: "",
    prezzo: "",
    costo_medio: "",
    aliquota_iva: "22",
    scorta_minima: "0",
    ubicazione: "",
    categoria: "",
    durata_stimata_ore: "1",
    note: ""
  });

  const [movimentoData, setMovimentoData] = useState({
    articolo_id: "",
    tipo: "carico",
    quantita: "",
    costo_unitario: "",
    note: ""
  });

  const fetchModuleConfig = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/modules/my-config`, { });
      if (res.ok) {
        const data = await res.json();
        setModuleConfig(data);
        if (!data.magazzino_enabled) {
          toast.error("Modulo Magazzino non abilitato");
        }
      }
    } catch (error) {
      console.error("Error fetching module config:", error);
    }
  }, []);

  const fetchData = useCallback(async () => {
    try {
      const [articoliRes, serviziRes, movimentiRes] = await Promise.all([
        authFetch(`${API}/modules/articoli?sotto_scorta=${showOnlySottoScorta}${searchTerm ? `&search=${searchTerm}` : ""}`, { }),
        authFetch(`${API}/modules/servizi${searchTerm ? `?search=${searchTerm}` : ""}`, { }),
        authFetch(`${API}/modules/movimenti?limit=50`, { })
      ]);

      if (articoliRes.ok) setArticoli(await articoliRes.json());
      if (serviziRes.ok) setServizi(await serviziRes.json());
      if (movimentiRes.ok) setMovimenti(await movimentiRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  }, [searchTerm, showOnlySottoScorta]);

  const fetchReport = useCallback(async () => {
    try {
      const res = await authFetch(`${API}/modules/magazzino/report`, { });
      if (res.ok) {
        setReport(await res.json());
      }
    } catch (error) {
      console.error("Error fetching report:", error);
    }
  }, []);

  useEffect(() => {
    fetchModuleConfig();
    fetchData();
  }, [fetchModuleConfig, fetchData]);

  useEffect(() => {
    if (activeTab === "report") {
      fetchReport();
    }
  }, [activeTab, fetchReport]);

  const handleCreateArticolo = async () => {
    try {
      const res = await authFetch(`${API}/modules/articoli`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codice: formData.codice,
          descrizione: formData.descrizione,
          prezzo_vendita: parseFloat(formData.prezzo_vendita) || 0,
          costo_medio: parseFloat(formData.costo_medio) || 0,
          aliquota_iva: parseFloat(formData.aliquota_iva) || 22,
          scorta_minima: parseInt(formData.scorta_minima) || 0,
          ubicazione: formData.ubicazione,
          categoria: formData.categoria,
          note: formData.note
        })
      });

      if (res.ok) {
        toast.success("Articolo creato con successo");
        setShowAddDialog(false);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleUpdateArticolo = async () => {
    try {
      const res = await authFetch(`${API}/modules/articoli/${editingItem.articolo_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codice: formData.codice,
          descrizione: formData.descrizione,
          prezzo_vendita: parseFloat(formData.prezzo_vendita) || 0,
          costo_medio: parseFloat(formData.costo_medio) || 0,
          aliquota_iva: parseFloat(formData.aliquota_iva) || 22,
          scorta_minima: parseInt(formData.scorta_minima) || 0,
          ubicazione: formData.ubicazione,
          categoria: formData.categoria,
          note: formData.note
        })
      });

      if (res.ok) {
        toast.success("Articolo aggiornato con successo");
        setShowAddDialog(false);
        setEditingItem(null);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleCreateServizio = async () => {
    try {
      const res = await authFetch(`${API}/modules/servizi`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codice: formData.codice,
          descrizione: formData.descrizione,
          prezzo: parseFloat(formData.prezzo) || 0,
          aliquota_iva: parseFloat(formData.aliquota_iva) || 22,
          durata_stimata_ore: parseFloat(formData.durata_stimata_ore) || 1,
          categoria: formData.categoria,
          note: formData.note
        })
      });

      if (res.ok) {
        toast.success("Servizio creato con successo");
        setShowAddDialog(false);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nella creazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleUpdateServizio = async () => {
    try {
      const res = await authFetch(`${API}/modules/servizi/${editingItem.servizio_id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          codice: formData.codice,
          descrizione: formData.descrizione,
          prezzo: parseFloat(formData.prezzo) || 0,
          aliquota_iva: parseFloat(formData.aliquota_iva) || 22,
          durata_stimata_ore: parseFloat(formData.durata_stimata_ore) || 1,
          categoria: formData.categoria,
          note: formData.note
        })
      });

      if (res.ok) {
        toast.success("Servizio aggiornato con successo");
        setShowAddDialog(false);
        setEditingItem(null);
        resetForm();
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nell'aggiornamento");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleCreateMovimento = async () => {
    try {
      const res = await authFetch(`${API}/modules/movimenti`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          articolo_id: movimentoData.articolo_id,
          tipo: movimentoData.tipo,
          quantita: parseInt(movimentoData.quantita) || 0,
          costo_unitario: parseFloat(movimentoData.costo_unitario) || 0,
          note: movimentoData.note
        })
      });

      if (res.ok) {
        toast.success("Movimento registrato con successo");
        setShowMovimentoDialog(false);
        setMovimentoData({ articolo_id: "", tipo: "carico", quantita: "", costo_unitario: "", note: "" });
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nella registrazione");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    }
  };

  const handleDeleteArticolo = async (articoloId) => {
    if (!window.confirm("Sei sicuro di voler disattivare questo articolo?")) return;
    
    try {
      const res = await authFetch(`${API}/modules/articoli/${articoloId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("Articolo disattivato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const handleDeleteServizio = async (servizioId) => {
    if (!window.confirm("Sei sicuro di voler disattivare questo servizio?")) return;
    
    try {
      const res = await authFetch(`${API}/modules/servizi/${servizioId}`, {
        method: "DELETE"
      });

      if (res.ok) {
        toast.success("Servizio disattivato");
        fetchData();
      }
    } catch (error) {
      toast.error("Errore");
    }
  };

  const openEditDialog = (item, type) => {
    setEditingItem(item);
    if (type === "articolo") {
      setFormData({
        codice: item.codice || "",
        descrizione: item.descrizione || "",
        prezzo_vendita: item.prezzo_vendita?.toString() || "",
        prezzo: "",
        costo_medio: item.costo_medio?.toString() || "",
        aliquota_iva: item.aliquota_iva?.toString() || "22",
        scorta_minima: item.scorta_minima?.toString() || "0",
        ubicazione: item.ubicazione || "",
        categoria: item.categoria || "",
        durata_stimata_ore: "1",
        note: item.note || ""
      });
    } else {
      setFormData({
        codice: item.codice || "",
        descrizione: item.descrizione || "",
        prezzo_vendita: "",
        prezzo: item.prezzo?.toString() || "",
        costo_medio: "",
        aliquota_iva: item.aliquota_iva?.toString() || "22",
        scorta_minima: "0",
        ubicazione: "",
        categoria: item.categoria || "",
        durata_stimata_ore: item.durata_stimata_ore?.toString() || "1",
        note: item.note || ""
      });
    }
    setShowAddDialog(true);
  };

  const resetForm = () => {
    setFormData({
      codice: "",
      descrizione: "",
      prezzo_vendita: "",
      prezzo: "",
      costo_medio: "",
      aliquota_iva: "22",
      scorta_minima: "0",
      ubicazione: "",
      categoria: "",
      durata_stimata_ore: "1",
      note: ""
    });
    setEditingItem(null);
  };

  const openInventarioDialog = () => {
    setInventarioData(articoli.map(a => ({
      articolo_id: a.articolo_id,
      codice: a.codice,
      descrizione: a.descrizione,
      giacenza_attuale: a.giacenza_calcolata || 0,
      giacenza: a.giacenza_calcolata || 0
    })));
    setShowInventarioDialog(true);
  };

  const handleInventario = async () => {
    const modificati = inventarioData.filter(i => i.giacenza !== i.giacenza_attuale);
    
    if (modificati.length === 0) {
      toast.info("Nessuna modifica da salvare");
      return;
    }

    setInventarioLoading(true);
    try {
      const res = await authFetch(`${API}/modules/magazzino/inventario`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ articoli: modificati })
      });

      if (res.ok) {
        const result = await res.json();
        toast.success(`Inventario completato: ${result.aggiornati} articoli aggiornati`);
        setShowInventarioDialog(false);
        fetchData();
      } else {
        const error = await res.json();
        toast.error(error.detail || "Errore nell'inventario");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setInventarioLoading(false);
    }
  };

  // Stats
  const articoliSottoScorta = articoli.filter(a => a.giacenza_calcolata < a.scorta_minima).length;
  const valoreStock = articoli.reduce((sum, a) => sum + (a.giacenza_calcolata * (a.costo_medio || 0)), 0);
  const totaleArticoli = articoli.length;

  if (!moduleConfig?.magazzino_enabled) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="magazzino-disabled">
        <div className="text-center">
          <Warehouse className="w-16 h-16 text-zinc-600 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-white mb-2">Modulo Magazzino non abilitato</h2>
          <p className="text-zinc-400">Contatta l'amministratore per attivare questo modulo.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="magazzino-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white uppercase font-['Barlow_Condensed'] tracking-wider flex items-center gap-2">
            <Warehouse className="w-6 h-6 text-primary" />
            Magazzino
          </h1>
          <p className="text-zinc-400 text-sm">Gestione articoli, servizi, movimenti e report</p>
        </div>
        
        <div className="flex gap-2 flex-wrap">
          {activeTab === "articoli" && (
            <Button variant="outline" onClick={openInventarioDialog} data-testid="btn-inventario">
              <ClipboardList className="w-4 h-4 mr-2" />
              Inventario
            </Button>
          )}
          <Dialog open={showMovimentoDialog} onOpenChange={setShowMovimentoDialog}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="btn-movimento">
                <ArrowUpDown className="w-4 h-4 mr-2" />
                Movimento
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800">
              <DialogHeader>
                <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">Registra Movimento</DialogTitle>
                <DialogDescription className="text-zinc-400">Registra un movimento di magazzino (carico, scarico, rettifica)</DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div>
                  <label className="text-sm text-zinc-400">Articolo</label>
                  <Select value={movimentoData.articolo_id} onValueChange={(v) => setMovimentoData({...movimentoData, articolo_id: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue placeholder="Seleziona articolo" />
                    </SelectTrigger>
                    <SelectContent>
                      {articoli.map(a => (
                        <SelectItem key={a.articolo_id} value={a.articolo_id}>
                          {a.codice} - {a.descrizione} (Giac: {a.giacenza_calcolata})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Tipo Movimento</label>
                  <Select value={movimentoData.tipo} onValueChange={(v) => setMovimentoData({...movimentoData, tipo: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="carico">Carico</SelectItem>
                      <SelectItem value="scarico">Scarico</SelectItem>
                      <SelectItem value="rettifica">Rettifica</SelectItem>
                      <SelectItem value="inventario">Inventario</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Quantita</label>
                    <Input
                      type="number"
                      value={movimentoData.quantita}
                      onChange={(e) => setMovimentoData({...movimentoData, quantita: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Costo Unitario</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={movimentoData.costo_unitario}
                      onChange={(e) => setMovimentoData({...movimentoData, costo_unitario: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Note</label>
                  <Input
                    value={movimentoData.note}
                    onChange={(e) => setMovimentoData({...movimentoData, note: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setShowMovimentoDialog(false)}>Annulla</Button>
                <Button onClick={handleCreateMovimento} data-testid="btn-registra-movimento">Registra</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          
          <Dialog open={showAddDialog} onOpenChange={(open) => { setShowAddDialog(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="btn-nuovo-articolo">
                <Plus className="w-4 h-4 mr-2" />
                Nuovo {activeTab === "articoli" ? "Articolo" : activeTab === "servizi" ? "Servizio" : ""}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
              <DialogHeader>
                <DialogTitle className="text-white uppercase font-['Barlow_Condensed']">
                  {editingItem ? "Modifica" : "Nuovo"} {activeTab === "articoli" ? "Articolo" : "Servizio"}
                </DialogTitle>
                <DialogDescription className="text-zinc-400">
                  {editingItem ? "Modifica i dati dell'elemento selezionato" : "Inserisci i dati del nuovo elemento"}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400">Codice *</label>
                    <Input
                      value={formData.codice}
                      onChange={(e) => setFormData({...formData, codice: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="es. ART001"
                      data-testid="input-codice"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400">Categoria</label>
                    <Input
                      value={formData.categoria}
                      onChange={(e) => setFormData({...formData, categoria: e.target.value})}
                      className="bg-zinc-800 border-zinc-700"
                      placeholder="es. Lubrificanti"
                      data-testid="input-categoria"
                    />
                  </div>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Descrizione *</label>
                  <Input
                    value={formData.descrizione}
                    onChange={(e) => setFormData({...formData, descrizione: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="input-descrizione"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  {(activeTab === "articoli" || editingItem?.articolo_id) ? (
                    <>
                      <div>
                        <label className="text-sm text-zinc-400">Prezzo Vendita *</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.prezzo_vendita}
                          onChange={(e) => setFormData({...formData, prezzo_vendita: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="input-prezzo-vendita"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-zinc-400">Costo Medio</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.costo_medio}
                          onChange={(e) => setFormData({...formData, costo_medio: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="input-costo-medio"
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label className="text-sm text-zinc-400">Prezzo *</label>
                        <Input
                          type="number"
                          step="0.01"
                          value={formData.prezzo}
                          onChange={(e) => setFormData({...formData, prezzo: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="input-prezzo"
                        />
                      </div>
                      <div>
                        <label className="text-sm text-zinc-400">Durata (ore)</label>
                        <Input
                          type="number"
                          step="0.5"
                          value={formData.durata_stimata_ore}
                          onChange={(e) => setFormData({...formData, durata_stimata_ore: e.target.value})}
                          className="bg-zinc-800 border-zinc-700"
                          data-testid="input-durata"
                        />
                      </div>
                    </>
                  )}
                </div>
                {(activeTab === "articoli" || editingItem?.articolo_id) && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-zinc-400">Scorta Minima</label>
                      <Input
                        type="number"
                        value={formData.scorta_minima}
                        onChange={(e) => setFormData({...formData, scorta_minima: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        data-testid="input-scorta-minima"
                      />
                    </div>
                    <div>
                      <label className="text-sm text-zinc-400">Ubicazione</label>
                      <Input
                        value={formData.ubicazione}
                        onChange={(e) => setFormData({...formData, ubicazione: e.target.value})}
                        className="bg-zinc-800 border-zinc-700"
                        placeholder="es. Scaffale A1"
                        data-testid="input-ubicazione"
                      />
                    </div>
                  </div>
                )}
                <div>
                  <label className="text-sm text-zinc-400">Aliquota IVA %</label>
                  <Select value={formData.aliquota_iva} onValueChange={(v) => setFormData({...formData, aliquota_iva: v})}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700" data-testid="select-iva">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="22">22%</SelectItem>
                      <SelectItem value="10">10%</SelectItem>
                      <SelectItem value="4">4%</SelectItem>
                      <SelectItem value="0">0% (Esente)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-sm text-zinc-400">Note</label>
                  <Input
                    value={formData.note}
                    onChange={(e) => setFormData({...formData, note: e.target.value})}
                    className="bg-zinc-800 border-zinc-700"
                    data-testid="input-note"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => {setShowAddDialog(false); resetForm();}}>Annulla</Button>
                <Button 
                  onClick={
                    editingItem 
                      ? (editingItem.articolo_id ? handleUpdateArticolo : handleUpdateServizio)
                      : (activeTab === "articoli" ? handleCreateArticolo : handleCreateServizio)
                  }
                  data-testid="btn-salva"
                >
                  {editingItem ? "Aggiorna" : "Salva"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card p-4 bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-3">
            <Package className="w-8 h-8 text-primary" />
            <div>
              <p className="text-zinc-400 text-sm">Totale Articoli</p>
              <p className="text-2xl font-bold text-white">{totaleArticoli}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-3">
            <AlertTriangle className={`w-8 h-8 ${articoliSottoScorta > 0 ? "text-red-500" : "text-green-500"}`} />
            <div>
              <p className="text-zinc-400 text-sm">Sotto Scorta</p>
              <p className="text-2xl font-bold text-white">{articoliSottoScorta}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-3">
            <BarChart3 className="w-8 h-8 text-blue-500" />
            <div>
              <p className="text-zinc-400 text-sm">Valore Stock</p>
              <p className="text-2xl font-bold text-white">{valoreStock.toFixed(2)}</p>
            </div>
          </div>
        </div>
        <div className="card p-4 bg-zinc-900/50 border border-zinc-800">
          <div className="flex items-center gap-3">
            <Clock className="w-8 h-8 text-purple-500" />
            <div>
              <p className="text-zinc-400 text-sm">Servizi</p>
              <p className="text-2xl font-bold text-white">{servizi.length}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-zinc-800 pb-2 overflow-x-auto">
        <Button 
          variant={activeTab === "articoli" ? "default" : "ghost"}
          onClick={() => setActiveTab("articoli")}
          className="flex items-center gap-2"
          data-testid="tab-articoli"
        >
          <Package className="w-4 h-4" />
          Articoli
        </Button>
        <Button 
          variant={activeTab === "servizi" ? "default" : "ghost"}
          onClick={() => setActiveTab("servizi")}
          className="flex items-center gap-2"
          data-testid="tab-servizi"
        >
          <Tag className="w-4 h-4" />
          Servizi
        </Button>
        <Button 
          variant={activeTab === "movimenti" ? "default" : "ghost"}
          onClick={() => setActiveTab("movimenti")}
          className="flex items-center gap-2"
          data-testid="tab-movimenti"
        >
          <ArrowUpDown className="w-4 h-4" />
          Movimenti
        </Button>
        <Button 
          variant={activeTab === "report" ? "default" : "ghost"}
          onClick={() => setActiveTab("report")}
          className="flex items-center gap-2"
          data-testid="tab-report"
        >
          <FileText className="w-4 h-4" />
          Report
        </Button>
      </div>

      {/* Search (not for report tab) */}
      {activeTab !== "report" && (
        <div className="flex gap-4 items-center">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
            <Input
              type="text"
              placeholder="Cerca per codice o descrizione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 bg-zinc-900/50 border-zinc-800"
              data-testid="input-search"
            />
          </div>
          {activeTab === "articoli" && (
            <Button
              variant={showOnlySottoScorta ? "default" : "outline"}
              onClick={() => setShowOnlySottoScorta(!showOnlySottoScorta)}
              className="flex items-center gap-2"
              data-testid="btn-sotto-scorta"
            >
              <AlertTriangle className="w-4 h-4" />
              Sotto Scorta
            </Button>
          )}
        </div>
      )}

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      ) : activeTab === "articoli" ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Codice</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Descrizione</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Categoria</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Prezzo</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Costo</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Giacenza</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Scorta Min</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {articoli.map((art) => (
                <tr key={art.articolo_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/30" data-testid={`articolo-row-${art.articolo_id}`}>
                  <td className="py-3 px-4 font-mono text-primary">{art.codice}</td>
                  <td className="py-3 px-4 text-white">{art.descrizione}</td>
                  <td className="py-3 px-4 text-zinc-400">{art.categoria || "-"}</td>
                  <td className="py-3 px-4 text-right text-white">{art.prezzo_vendita?.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right text-zinc-400">{art.costo_medio?.toFixed(2)}</td>
                  <td className={`py-3 px-4 text-right font-bold ${art.giacenza_calcolata < art.scorta_minima ? "text-red-500" : "text-green-500"}`}>
                    {art.giacenza_calcolata}
                  </td>
                  <td className="py-3 px-4 text-right text-zinc-400">{art.scorta_minima}</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => openEditDialog(art, "articolo")}
                        data-testid={`btn-edit-${art.articolo_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleDeleteArticolo(art.articolo_id)}
                        data-testid={`btn-delete-${art.articolo_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {articoli.length === 0 && (
                <tr>
                  <td colSpan={8} className="py-12 text-center text-zinc-500">
                    Nessun articolo trovato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : activeTab === "servizi" ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Codice</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Descrizione</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Categoria</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Prezzo</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Durata</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">IVA</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Azioni</th>
              </tr>
            </thead>
            <tbody>
              {servizi.map((srv) => (
                <tr key={srv.servizio_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/30" data-testid={`servizio-row-${srv.servizio_id}`}>
                  <td className="py-3 px-4 font-mono text-primary">{srv.codice}</td>
                  <td className="py-3 px-4 text-white">{srv.descrizione}</td>
                  <td className="py-3 px-4 text-zinc-400">{srv.categoria || "-"}</td>
                  <td className="py-3 px-4 text-right text-white">{srv.prezzo?.toFixed(2)}</td>
                  <td className="py-3 px-4 text-right text-zinc-400">{srv.durata_stimata_ore}h</td>
                  <td className="py-3 px-4 text-right text-zinc-400">{srv.aliquota_iva}%</td>
                  <td className="py-3 px-4 text-right">
                    <div className="flex justify-end gap-1">
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8"
                        onClick={() => openEditDialog(srv, "servizio")}
                        data-testid={`btn-edit-srv-${srv.servizio_id}`}
                      >
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        className="h-8 w-8 text-red-500"
                        onClick={() => handleDeleteServizio(srv.servizio_id)}
                        data-testid={`btn-delete-srv-${srv.servizio_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
              {servizi.length === 0 && (
                <tr>
                  <td colSpan={7} className="py-12 text-center text-zinc-500">
                    Nessun servizio trovato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : activeTab === "movimenti" ? (
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-zinc-800">
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Data</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Tipo</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Articolo</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Quantita</th>
                <th className="text-right py-3 px-4 text-zinc-400 font-medium">Costo Unit.</th>
                <th className="text-left py-3 px-4 text-zinc-400 font-medium">Utente</th>
              </tr>
            </thead>
            <tbody>
              {movimenti.map((mov) => (
                <tr key={mov.movimento_id} className="border-b border-zinc-800/50 hover:bg-zinc-900/30">
                  <td className="py-3 px-4 text-zinc-400 font-mono text-sm">
                    {new Date(mov.created_at).toLocaleString("it-IT")}
                  </td>
                  <td className="py-3 px-4">
                    <span className={`px-2 py-1 rounded text-xs font-bold uppercase ${
                      mov.tipo === "carico" ? "bg-green-500/20 text-green-400" :
                      mov.tipo === "scarico" ? "bg-red-500/20 text-red-400" :
                      mov.tipo === "rettifica" ? "bg-yellow-500/20 text-yellow-400" :
                      "bg-blue-500/20 text-blue-400"
                    }`}>
                      {mov.tipo}
                    </span>
                  </td>
                  <td className="py-3 px-4">
                    <div>
                      <span className="text-primary font-mono">{mov.articolo_codice}</span>
                      <p className="text-zinc-400 text-sm truncate max-w-xs">{mov.articolo_descrizione}</p>
                    </div>
                  </td>
                  <td className={`py-3 px-4 text-right font-bold ${
                    mov.tipo === "carico" ? "text-green-500" : 
                    mov.tipo === "scarico" ? "text-red-500" : "text-white"
                  }`}>
                    {mov.tipo === "scarico" ? "-" : "+"}{mov.quantita}
                  </td>
                  <td className="py-3 px-4 text-right text-zinc-400">
                    {mov.costo_unitario > 0 ? `${mov.costo_unitario.toFixed(2)}` : "-"}
                  </td>
                  <td className="py-3 px-4 text-zinc-400">{mov.utente_nome}</td>
                </tr>
              ))}
              {movimenti.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-zinc-500">
                    Nessun movimento registrato
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      ) : activeTab === "report" && report ? (
        <div className="space-y-6" data-testid="report-section">
          {/* Riepilogo */}
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Package className="w-4 h-4 text-primary" />
                  <span className="text-xs text-zinc-400">Articoli</span>
                </div>
                <p className="text-xl font-bold text-white">{report.riepilogo.totale_articoli}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Tag className="w-4 h-4 text-purple-500" />
                  <span className="text-xs text-zinc-400">Servizi</span>
                </div>
                <p className="text-xl font-bold text-white">{report.riepilogo.totale_servizi}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <BarChart3 className="w-4 h-4 text-blue-500" />
                  <span className="text-xs text-zinc-400">Valore Magazzino</span>
                </div>
                <p className="text-xl font-bold text-white">{report.riepilogo.valore_magazzino.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-green-500" />
                  <span className="text-xs text-zinc-400">Valore Vendita</span>
                </div>
                <p className="text-xl font-bold text-white">{report.riepilogo.valore_vendita.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <Box className="w-4 h-4 text-yellow-500" />
                  <span className="text-xs text-zinc-400">Margine Potenziale</span>
                </div>
                <p className="text-xl font-bold text-white">{report.riepilogo.margine_potenziale.toFixed(2)}</p>
              </CardContent>
            </Card>
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-1">
                  <TrendingUp className="w-4 h-4 text-emerald-500" />
                  <span className="text-xs text-zinc-400">Margine %</span>
                </div>
                <p className="text-xl font-bold text-white">{report.riepilogo.margine_percentuale}%</p>
              </CardContent>
            </Card>
          </div>

          {/* Alert e Movimenti */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Alert */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <AlertCircle className="w-5 h-5 text-red-500" />
                  Alert Magazzino
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  <div className="flex justify-between items-center p-3 bg-red-500/10 border border-red-500/20 rounded">
                    <span className="text-zinc-300">Articoli sotto scorta</span>
                    <Badge variant="destructive">{report.alert.sotto_scorta}</Badge>
                  </div>
                  <div className="flex justify-between items-center p-3 bg-yellow-500/10 border border-yellow-500/20 rounded">
                    <span className="text-zinc-300">Articoli esauriti</span>
                    <Badge className="bg-yellow-500/20 text-yellow-400">{report.alert.esauriti}</Badge>
                  </div>
                  {report.alert.articoli_sotto_scorta.length > 0 && (
                    <div className="mt-4">
                      <p className="text-sm text-zinc-400 mb-2">Da riordinare:</p>
                      <div className="space-y-1 max-h-40 overflow-y-auto">
                        {report.alert.articoli_sotto_scorta.map((a, i) => (
                          <div key={i} className="flex justify-between text-sm p-2 bg-zinc-800/50 rounded">
                            <span className="text-white truncate flex-1">{a.codice} - {a.descrizione}</span>
                            <span className="text-red-400 ml-2">{a.giacenza}/{a.scorta_minima}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Movimenti 30 giorni */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <ArrowUpDown className="w-5 h-5 text-blue-500" />
                  Movimenti Ultimi 30 Giorni
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="p-3 bg-green-500/10 border border-green-500/20 rounded text-center">
                      <p className="text-2xl font-bold text-green-400">{report.movimenti_30g.totale_carichi}</p>
                      <p className="text-xs text-zinc-400">Carichi</p>
                      <p className="text-sm text-green-400 mt-1">{report.movimenti_30g.valore_carichi.toFixed(2)}</p>
                    </div>
                    <div className="p-3 bg-red-500/10 border border-red-500/20 rounded text-center">
                      <p className="text-2xl font-bold text-red-400">{report.movimenti_30g.totale_scarichi}</p>
                      <p className="text-xs text-zinc-400">Scarichi</p>
                      <p className="text-sm text-red-400 mt-1">{report.movimenti_30g.valore_scarichi.toFixed(2)}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Categorie e Top Rotazione */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Categorie */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <Tag className="w-5 h-5 text-purple-500" />
                  Valore per Categoria
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {report.categorie.slice(0, 8).map((cat, i) => (
                    <div key={i} className="space-y-1">
                      <div className="flex justify-between text-sm">
                        <span className="text-white">{cat.nome}</span>
                        <span className="text-zinc-400">{cat.valore.toFixed(2)} ({cat.count} art.)</span>
                      </div>
                      <Progress 
                        value={report.riepilogo.valore_magazzino > 0 ? (cat.valore / report.riepilogo.valore_magazzino * 100) : 0} 
                        className="h-2"
                      />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Top Rotazione */}
            <Card className="bg-zinc-900/50 border-zinc-800">
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2 text-white">
                  <RefreshCw className="w-5 h-5 text-emerald-500" />
                  Top 10 Articoli Movimentati
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {report.top_rotazione.length === 0 ? (
                    <p className="text-zinc-500 text-sm">Nessun movimento negli ultimi 30 giorni</p>
                  ) : (
                    report.top_rotazione.map((item, i) => (
                      <div key={i} className="flex items-center gap-3 p-2 bg-zinc-800/30 rounded">
                        <span className="text-primary font-bold w-6">{i + 1}</span>
                        <div className="flex-1 min-w-0">
                          <p className="text-white text-sm truncate">{item.codice}</p>
                          <p className="text-zinc-400 text-xs truncate">{item.descrizione}</p>
                        </div>
                        <Badge className="bg-emerald-500/20 text-emerald-400">{item.quantita_scaricata}</Badge>
                      </div>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : null}

      {/* Inventario Dialog */}
      <Dialog open={showInventarioDialog} onOpenChange={setShowInventarioDialog}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-4xl max-h-[80vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="text-white uppercase font-['Barlow_Condensed'] flex items-center gap-2">
              <ClipboardList className="w-5 h-5 text-primary" />
              Inventario Magazzino
            </DialogTitle>
            <DialogDescription className="text-zinc-400">
              Modifica le giacenze degli articoli e salva per registrare i movimenti di inventario
            </DialogDescription>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto py-4">
            <table className="w-full">
              <thead className="sticky top-0 bg-zinc-900">
                <tr className="border-b border-zinc-800">
                  <th className="text-left py-2 px-3 text-zinc-400 font-medium text-sm">Codice</th>
                  <th className="text-left py-2 px-3 text-zinc-400 font-medium text-sm">Descrizione</th>
                  <th className="text-right py-2 px-3 text-zinc-400 font-medium text-sm">Giacenza Attuale</th>
                  <th className="text-right py-2 px-3 text-zinc-400 font-medium text-sm">Nuova Giacenza</th>
                  <th className="text-center py-2 px-3 text-zinc-400 font-medium text-sm">Diff</th>
                </tr>
              </thead>
              <tbody>
                {inventarioData.map((item, idx) => {
                  const diff = item.giacenza - item.giacenza_attuale;
                  return (
                    <tr key={item.articolo_id} className="border-b border-zinc-800/50">
                      <td className="py-2 px-3 font-mono text-primary text-sm">{item.codice}</td>
                      <td className="py-2 px-3 text-white text-sm truncate max-w-xs">{item.descrizione}</td>
                      <td className="py-2 px-3 text-right text-zinc-400">{item.giacenza_attuale}</td>
                      <td className="py-2 px-3 text-right">
                        <Input
                          type="number"
                          value={item.giacenza}
                          onChange={(e) => {
                            const newData = [...inventarioData];
                            newData[idx].giacenza = parseInt(e.target.value) || 0;
                            setInventarioData(newData);
                          }}
                          className="w-24 h-8 bg-zinc-800 border-zinc-700 text-right"
                        />
                      </td>
                      <td className="py-2 px-3 text-center">
                        {diff !== 0 && (
                          <span className={`font-bold ${diff > 0 ? "text-green-500" : "text-red-500"}`}>
                            {diff > 0 ? "+" : ""}{diff}
                          </span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <DialogFooter className="border-t border-zinc-800 pt-4">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm text-zinc-400">
                {inventarioData.filter(i => i.giacenza !== i.giacenza_attuale).length} articoli modificati
              </p>
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => setShowInventarioDialog(false)}>Annulla</Button>
                <Button onClick={handleInventario} disabled={inventarioLoading} data-testid="btn-salva-inventario">
                  {inventarioLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Salva Inventario
                </Button>
              </div>
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
